import { useContext, useEffect } from "react"
import { create } from "zustand"

import Toast from "@/topic/rogue_5/components/Toast"
import { GlobalContext } from "@/topic/rogue_5/context"
import {
    IGameCopper,
    IGameResult,
    useTopicI18nStore,
} from "@/topic/rogue_5/locales"
import { gameGetInfo } from "@/topic/rogue_5/service"
import { IRogue5GameInfo } from "@/topic/rogue_5/types"
import { randomRow } from "@/topic/rogue_5/utils/misc"

export enum EGameScene {
    Loading = "Loading",
    Lobby = "Lobby",
    Game = "Game",
    Result = "Result",
    Record = "Record",
}

export interface IGameRandomResult {
    relics: string[]
    characters: string[]
    band: string
    recruitGroup: string
}

const SPECIAL_CHARACTERS = [
    "char_4141_marcil",
    "char_4142_laios",
    "char_4144_chilc",
    "char_4143_sensi",
    "char_4123_ela",
    "char_4125_rdoc",
    "char_4124_iana",
    "char_4126_fuze",
    "char_1029_yato2",
    "char_1030_noirc2",
    "char_4077_palico",
    "char_4067_lolxh",
    "char_4019_ncdeer",
    "char_456_ash",
    "char_458_rfrost",
    "char_457_blitz",
    "char_459_tachak",
]

const getRandomFromList = (list: string[], count: number): string[] => {
    if (list.length < count) {
        return list
    }
    const result: string[] = []
    for (let i = 0; i < count; i++) {
        const index = Math.floor(Math.random() * list.length)
        result.push(list[index])
        list.splice(index, 1)
    }
    return result
}

const getRandomResults = (): IGameRandomResult => {
    // relics
    const randomRelicList =
        useTopicI18nStore.getState().objects.gameRandomDict.relics
    const list = randomRelicList.slice()
    const relics: string[] = getRandomFromList(list, 3)

    // characters
    const listCharacters = useGameStore
        .getState()
        .userCharacters.slice()
        .filter((s) => !SPECIAL_CHARACTERS.includes(s))
    const characters: string[] = getRandomFromList(listCharacters, 3)

    // bands
    const listBands = useTopicI18nStore.getState().objects.gameRandomDict.band
    const band: string = getRandomFromList(listBands, 1)[0]

    // recruit groups
    const listRecruitGroups =
        useTopicI18nStore.getState().objects.gameRandomDict.recruitGroups
    const recruitGroup: string = getRandomFromList(listRecruitGroups, 1)[0]

    return { relics, characters, band, recruitGroup }
}

export interface IGameState {
    // 流程控制
    scene: EGameScene
    setScene: (scene: EGameScene) => void

    // 游戏数据
    gameType: "normal" | "rhodes" | null
    gameResult: { coins: IGameCopper[]; result: IGameResult } | null
    gameRandomResult: IGameRandomResult | null

    rowGameResult: () => { coins: IGameCopper[]; result: IGameResult }
    resetGameResult: () => void

    info: IRogue5GameInfo
    canRoll: boolean

    refreshInfo: () => void

    // ui
    uiActive: boolean
    showUI: (active: boolean) => void
    boxButtonActive: boolean
    showBoxButton: (active: boolean) => void
    giftEntryActive: boolean
    showGiftEntry: (active: boolean) => void

    restarted?: boolean

    userCharacters: string[]
}

export const useGameStore = create<IGameState>((set) => ({
    scene: EGameScene.Loading,
    setScene: (scene: EGameScene) => set({ scene }),

    gameType: "normal" as const,
    coins: [],
    gameResult: null,
    gameRandomResult: null,
    canRoll: true,
    rowGameResult: () => {
        const randomResult =
            useGameStore.getState().gameType === "rhodes"
                ? getRandomResults()
                : null
        const COINS = useTopicI18nStore.getState().objects.gameCopper
        const RESULT = useTopicI18nStore.getState().objects.gameResult
        // 随机硬币种类
        const cnt = {
            good: 0,
            normal: 0,
            bad: 0,
        }
        for (let i = 0; i < 3; i++) {
            const rand = Math.random()
            if (rand < 0.34) {
                cnt.good++
            } else if (rand < 0.67) {
                cnt.normal++
            } else {
                cnt.bad++
            }
        }

        const goodCoins = Object.values(COINS).filter((c) => c.type === "good")
        const normalCoins = Object.values(COINS).filter(
            (c) => c.type === "normal"
        )
        const badCoins = Object.values(COINS).filter((c) => c.type === "bad")

        const coins = [
            ...randomRow(goodCoins, cnt.good),
            ...randomRow(normalCoins, cnt.normal),
            ...randomRow(badCoins, cnt.bad),
        ]

        // const num = {
        //     good: coins.filter((c) => c.type === "good").length,
        //     bad: coins.filter((c) => c.type === "bad").length,
        //     normal: coins.filter((c) => c.type === "normal").length,
        // }
        const result = RESULT[`${cnt.good}-${cnt.normal}-${cnt.bad}`]
        const gameResult = { coins, result }
        set({ gameResult, gameRandomResult: randomResult })
        return gameResult
    },
    resetGameResult: () => {
        set({ gameResult: null, restarted: true })
    },
    info: {
        game: { complete: false },
        signInRecord: [],
        roll: { used: false, awardId: "" },
    },
    refreshInfo: () => {
        const reset = () =>
            set({
                info: {
                    game: { complete: false },
                    roll: {
                        used: false,
                        awardId: "",
                    },
                    signInRecord: [],
                },
            })
        gameGetInfo()
            .then((res) => {
                if (res.code === 0) {
                    if (res.data?.signInRecord?.length > 0) {
                        set({
                            boxButtonActive: true,
                        })
                    } else {
                        set({
                            boxButtonActive: false,
                        })
                    }
                    if (res.data?.roll?.used) {
                        set({
                            giftEntryActive: true,
                        })
                    } else {
                        set({
                            giftEntryActive: false,
                        })
                    }
                    useGameStore.setState({
                        info: {
                            game: { complete: res.data.game.complete },
                            roll: {
                                used: res.data.roll.used,
                                awardId: res.data.roll.awardId,
                            },
                            signInRecord: res.data.signInRecord,
                            contact: res.data.contact,
                        },
                        userCharacters: res.data.characters ?? [],
                    })
                } else {
                    Toast.message({ content: res.msg })
                }
            })
            .catch((err) => {
                console.log(err)
                reset()
            })
    },
    uiActive: false,
    showUI: (active: boolean) => {
        set({ uiActive: active })
    },
    boxButtonActive: false,
    showBoxButton: (active: boolean) => {
        set({ boxButtonActive: active })
    },
    giftEntryActive: false,
    showGiftEntry: (active: boolean) => {
        set({ giftEntryActive: active })
    },

    restarted: false,

    userCharacters: [],
}))

export const useShowUI = () => useGameStore((state) => state.showUI)
export const useShowBoxButton = () =>
    useGameStore((state) => state.showBoxButton)
export const useRefreshInfo = () => useGameStore((state) => state.refreshInfo)

export const useGameStoreInit = () => {
    const {
        state: { user },
    } = useContext(GlobalContext)
    const refreshInfo = useRefreshInfo()
    useEffect(() => {
        if (user) {
            useGameStore.setState({
                canRoll: true,
                gameResult: null,
                restarted: false,
                scene: EGameScene.Loading,
            })
            refreshInfo()
        }
    }, [user])
}
